
import Component from 'vue-class-component'
import { Prop, Vue, Watch } from 'vue-property-decorator'

/**
 * This component functions as a validation guard that works with [`checkValidity()`](https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity).
 *
 * It renders:
 *  - a hidden required checkbox input when `isEnabled` is `true`,
 *  - and a <TixMessage> error message.
 *
 * The checkbox serves as an invisible validation hook, allowing the component
 * to participate in form validation via `reportFormValidity()`.
 *
 * The error message is conditionally displayed when:
 *  - `message` is not null or empty,
 *  - and the component is marked as invalid (`markedInvalid` is `true`)
 *
 *  The component can be marked as invalid by `checkValidity()`
 *  when the component is enabled (`isEnabled` is `true`).
 *
 * ⚠️ Caveats:
 *  - If this component is conditionally rendered (e.g., via `v-if`)
 *  after an asynchronous operation (e.g., a network request),
 *  you must take precautions (e.g., disable the submit button while loading)
 *  to avoid false positives from the `reportFormValidity()` helper.
 *  - If this component is nested in `<form>` element, make sure that `novalidate`
 *  attribute is present on the `<form>` element to ensure that the native validation message
 *  doesn't show.
 */
@Component({
  name: 'FormValidationGuard',
  components: {},
})
export default class extends Vue {
  @Prop({ required: true })
  isEnabled: boolean

  @Prop({ required: true })
  message: string | null

  private markedInvalid = false

  private markInvalid(invalid: boolean): void {
    this.markedInvalid = invalid
    this.$emit('invalid', invalid)
  }

  onInvalid(): void {
    if (!this.isEnabled) return

    // Ensure screen readers announce the error message each time @invalid fires
    this.markInvalid(false)
    this.$nextTick(() => {
      this.markInvalid(true)
    })
  }

  @Watch('isEnabled')
  onGuardChange(isEnabled: boolean) {
    // Do not show the message when `isEnabled` has changed from `false` to `true`
    // (e.g. when a selected date is cleared)
    if (isEnabled) {
      this.markedInvalid = false
    }
  }

  get showMessage() {
    return this.markedInvalid && this.message
  }
}
